import { type MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import FishPenLine from '@/assets/icons/fish_pen_line.svg?component';
import { Overline } from '@/components/atoms/Eyebrow';
import { formatDateShortLocalized } from '@/utils/date';

import { clusterBadges } from '../../helpers/badgeClusters';
import type { FocusSource } from '../../helpers/lineFocus';
import { layoutTrackLabels } from '../../helpers/trackLabels';
import { trackStyle } from '../../helpers/trackStyle';
import { stayWeightText } from '../../helpers/weightText';
import type { BarSegment, GroupRowModel } from '../../types';
import EventBadge from '../EventBadge/EventBadge';
import Glyph from '../Glyph/Glyph';

import PenNameTrigger from './PenNameTrigger';
import TrackTip from './TrackTip';

import styles from './GroupTrack.module.scss';

interface GroupTrackProps {
    row: GroupRowModel;
    /** Shown on a result row while its sources are collapsed, so the operation stays visible. */
    showCreationBadge: boolean;
    /**
     * Opens a group's dialog from a click on its lane: on a chain row, the group whose bar was
     * clicked, else the row's own. The name and the "N groups" menu are the keyboard path.
     */
    onOpen?: (groupId: number) => void;
    /**
     * The track's measured width; its labels are laid out again for it. Null until measured,
     * when the row keeps the layout for the narrowest track.
     */
    trackPx?: number | null;
    /** A zoomed-out long cycle: badges and markers draw without their labels. */
    compactLabels?: boolean;
    /** The visible track's width: a current stay within it names its pen on the chip only. */
    viewPx?: number | null;
    /** The groups the active lines join: these groups' bars are outlined. */
    emphasisedGroups?: ReadonlySet<number>;
    /**
     * The group under the pointer, or holding keyboard focus, on this lane, by the bar (or its
     * label or event) it is on; null once it leaves. On a chain row that's the member, not the
     * row's latest. The pointer and the keyboard are reported separately.
     */
    onGroupFocus?: (source: FocusSource, groupId: number | null) => void;
}

/** One group's lane: a bar per pen stay, hatched where no bioscope was in the pen. */
export default function GroupTrack(props: GroupTrackProps) {
    const { showCreationBadge, onOpen, trackPx = null, compactLabels = false } = props;
    const { viewPx = null, emphasisedGroups, onGroupFocus } = props;
    const { creationBadge } = props.row;
    // A redistribution is marked on every group in it, so its badge stays on a destination
    // while the sources show; other creations are marked only while their sources are hidden.
    const creationShown =
        creationBadge !== null &&
        (showCreationBadge || creationBadge.kinds.includes('redistribution'));
    // A shown creation badge is one of the row's badges, so it clusters with the events near
    // it and takes part in label spacing rather than being drawn over them.
    const row = useMemo(() => {
        const badges =
            creationShown && creationBadge
                ? [...props.row.badges, creationBadge]
                : props.row.badges;
        if (!trackPx) return { ...props.row, badges };
        return layoutTrackLabels(
            { ...props.row, badges: clusterBadges(badges, trackPx) },
            trackPx,
            compactLabels,
            viewPx ?? undefined
        );
    }, [props.row, creationBadge, creationShown, trackPx, compactLabels, viewPx]);
    const { t, i18n } = useTranslation('fishGroups');
    const liveMembers = new Set(
        row.members.filter((member) => member.isLive).map((member) => member.groupId)
    );
    const toneOf = (segment: BarSegment) =>
        (segment.groupId === undefined ? row.isLive : liveMembers.has(segment.groupId))
            ? styles.segmentActive
            : styles.segmentCompleted;
    const openFromClick = (event: MouseEvent<HTMLDivElement>) => {
        if (!onOpen) return;
        const bar =
            event.target instanceof Element ? event.target.closest('[data-group-id]') : null;
        const groupId = bar ? Number(bar.getAttribute('data-group-id')) : row.groupId;
        onOpen(Number.isInteger(groupId) ? groupId : row.groupId);
    };
    // Anything drawn on the lane belongs to a group; the lane's empty stretches don't.
    const groupAt = (target: EventTarget, lane: Element): number | null => {
        if (!(target instanceof Element) || target === lane) return null;
        const owner = target.closest('[data-group-id]');
        const groupId = owner ? Number(owner.getAttribute('data-group-id')) : row.groupId;
        return Number.isInteger(groupId) ? groupId : row.groupId;
    };
    const reach =
        (source: FocusSource) => (event: { target: EventTarget; currentTarget: Element }) =>
            onGroupFocus?.(source, groupAt(event.target, event.currentTarget));
    const segmentTitle = (segment: BarSegment) => {
        const status = segment.planned
            ? t('board.legend.planned')
            : segment.noBioscope
              ? t('board.legend.noBioscope')
              : null;
        const weight = stayWeightText(segment.weight, i18n.language, t);
        return [status, weight].reduce<string>(
            (title, part) =>
                part ? t('board.row.segmentTitle', { pen: title, status: part }) : title,
            segment.penName
        );
    };

    return (
        // A pointer shortcut: keyboard and screen-reader users open the dialog from the name.
        <div
            className={classNames(styles.track, { [styles.trackOpenable]: onOpen })}
            onClick={openFromClick}
            onMouseOver={reach('pointer')}
            onMouseLeave={() => onGroupFocus?.('pointer', null)}
            onFocus={reach('keyboard')}
            onBlur={() => onGroupFocus?.('keyboard', null)}>
            {row.segments.map((segment) => {
                const className = classNames(styles.segment, toneOf(segment), {
                    [styles.segmentNoBioscope]: segment.noBioscope,
                    [styles.segmentPlanned]: segment.planned,
                    [styles.segmentJoinsPrevious]: segment.joinsPrevious,
                    [styles.segmentJoinsNext]: segment.joinsNext,
                    [styles.segmentEmphasised]: emphasisedGroups?.has(
                        segment.groupId ?? row.groupId
                    ),
                });
                const style = trackStyle(segment.leftPct, segment.widthPct);
                return segment.revealsPen ? (
                    <div
                        key={segment.key}
                        className={className}
                        style={style}
                        data-group-id={segment.groupId ?? row.groupId}>
                        <PenNameTrigger label={segmentTitle(segment)} />
                    </div>
                ) : (
                    <span
                        key={segment.key}
                        className={className}
                        style={style}
                        data-group-id={segment.groupId ?? row.groupId}>
                        <TrackTip content={segmentTitle(segment)} />
                    </span>
                );
            })}
            {row.links.map((link) => (
                <span
                    key={link.key}
                    className={styles.chainLinkSpot}
                    style={trackStyle(link.leftPct)}
                    data-group-id={link.groupId}>
                    <TrackTip
                        content={[
                            t('board.row.chainLink', {
                                from: link.fromName,
                                to: link.toName,
                                date: formatDateShortLocalized(link.date, i18n.language),
                            }),
                            link.kind && t(`board.events.${link.kind}`),
                        ]
                            .filter(Boolean)
                            .join(t('board.banner.separator'))}>
                        <span className={styles.chainLink} />
                    </TrackTip>
                </span>
            ))}
            {row.penTags.map(
                (tag, index) =>
                    !tag.hidden && (
                        <span
                            key={tag.key}
                            className={classNames(styles.penTag, {
                                [styles.penTagAfterMove]:
                                    tag.afterMove || (index === 0 && creationShown),
                            })}
                            style={trackStyle(tag.leftPct)}
                            data-group-id={tag.groupId}>
                            <Glyph icon={FishPenLine} />
                            {tag.penName}
                        </span>
                    )
            )}
            {row.markers.map((marker) => (
                <span
                    key={marker.key}
                    className={classNames(styles.marker, styles[`marker--${marker.kind}`])}
                    style={trackStyle(marker.leftPct)}
                    data-group-id={marker.groupId}>
                    <span className={styles.lineHit}>
                        <TrackTip
                            content={
                                marker.kind === 'firstMeasurement' && marker.date
                                    ? t('board.markers.firstMeasurementOn', {
                                          date: formatDateShortLocalized(
                                              marker.date,
                                              i18n.language
                                          ),
                                      })
                                    : t(`board.markers.${marker.kind}`)
                            }
                        />
                    </span>
                    {marker.labelKinds.length > 0 && (
                        <Overline className={styles.markerLabel}>
                            {marker.labelKinds
                                .map((kind) => t(`board.markers.${kind}`))
                                .join(t('board.events.separator'))}
                        </Overline>
                    )}
                </span>
            ))}
            {row.ticks.map((tick) => (
                <span
                    key={tick.key}
                    className={styles.tick}
                    style={trackStyle(tick.leftPct)}
                    data-group-id={tick.groupId}>
                    <span className={styles.lineHit}>
                        <TrackTip content={t('board.legend.partialHarvest')} />
                    </span>
                </span>
            ))}
            {row.badges.map((badge) => (
                <span key={badge.key} className={styles.owner} data-group-id={badge.groupId}>
                    <EventBadge
                        kinds={badge.kinds}
                        leftPct={badge.leftPct}
                        peers={badge.peers}
                        showLabel={badge.labelShown}
                    />
                </span>
            ))}
            {row.currentPen && (
                <span className={styles.penNow} style={trackStyle(row.endPct)}>
                    <TrackTip content={t('board.row.currentPen')}>
                        <span className={styles.penNowContent}>
                            <Glyph icon={FishPenLine} />
                            <span className={styles.visuallyHidden}>
                                {t('board.row.currentPen')}:{' '}
                            </span>
                            {row.currentPen}
                        </span>
                    </TrackTip>
                </span>
            )}
        </div>
    );
}
